import React, { useRef, useEffect, useState, useMemo } from "react";
import { Modal, Col, Row, Form } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation, useLazyQuery } from "@apollo/client";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Checkbox,
  Typography,
  Button,
} from "@material-ui/core";
import { Formik } from "formik";

import {
  setIsShowTNCDialog,
  setShowOrgModal,
  setShowProfilePopper,
} from "../../../redux/layout/layoutActions";
import { useCurrentUserUIContext } from "../../Components/CurrentUser/CurrentUserContext";
import {
  CREATE_ORGANIZATION,
  JOIN_ORGANIZATION,
  MERGE_ORGANIZATION_REQUEST,
  MERGE_ORGANIZATION,
} from "../../GraphQlCommon/mutation";
import { UPDATE_EMPLOYEE_APPROVE } from "../../Pages/Employees/graphQl/mutation";
import { GET_EMPLOYEES } from "../../Pages/Employees/graphQl/query";
import { GET_ORGANIZATIONS } from "../../Pages/Organizations/Graphql/query";
import { TncDialog } from "../../Pages/Selling/seller/screen/TncDialog";
import { NEGOTIATION_CALLING_ACTION, ROLE_GUEST } from "../../Utils/constant";
import { page360Url } from "../../Utils/helper";
import { SectionLoader } from "../SectionLoader";

import { useOrganizationModalValidationSchema } from "./useOrganizationModalValidationSchema";

export const OrganizationModal = () => {
  const {
    initialValues,
    organizationModalValidationSchema,
  } = useOrganizationModalValidationSchema();

  const dispatch = useDispatch();

  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(null);

  const [isOrganizationExist, setIsOrganizationExist] = useState(false);

  const [existingOrganization, setExistingOrganization] = useState(null);

  const [newOrganization, setNewOrganization] = useState(null);

  const [checked, setChecked] = useState(false);

  const [showMergeContentAlert, setShowMergeContentAlert] = useState(false);

  const [isUsersExist, setIsUsersExist] = useState(false);

  const [isReqOrgBuyer, setIsReqOrgBuyer] = useState(false);

  const [value, setValue] = useState();

  const [filterOrganization, setFilterOrganization] = useState();

  const formikRef = useRef();

  const intl = useIntl();

  const history = useHistory();

  const { currentStateAuth } = useSelector(
    state => ({ currentStateAuth: state.auth }),
    shallowEqual,
  );

  const { user, assetDetail } = currentStateAuth;

  // Getting current state of layout from store (Redux)
  const { showOrgModal } = useSelector(
    state => ({ showOrgModal: state.layout.showOrgModal }),
    shallowEqual,
  );

  const { isShowTNCDialog } = useSelector(
    state => ({ isShowTNCDialog: state.layout.isShowTNCDialog }),
    shallowEqual,
  );

  const currentUserContext = useCurrentUserUIContext();

  const currentUserUIProps = useMemo(() => {
    return {
      currentUser: currentUserContext.currentUser,
      setCurrentUser: currentUserContext.setCurrentUser,
      currentUserError: currentUserContext.currentUserError,
    };
  }, [currentUserContext]);

  const handleModelClose = () => {
    dispatch(setShowOrgModal(false));
    setIsOrganizationExist(false);
    setExistingOrganization(false);
    setNewOrganization(false);
    setShowMergeContentAlert(false);
    setIsUsersExist(false);
    setIsReqOrgBuyer(false);
    setFilterOrganization(false);
    dispatch(setShowProfilePopper(null));
  };

  const [
    createOrg,
    { loading: createOrgLoading, error: createOrgError, data: createOrgData },
  ] = useMutation(CREATE_ORGANIZATION);
  
  const [
    joinOrg,
    { loading: joinOrgLoading, error: joinOrgError, data: joinOrgData },
  ] = useMutation(JOIN_ORGANIZATION);

  const [
    mergeOrg,
    { loading: mergeOrgLoading, error: mergeOrgError, data: mergeOrgData },
  ] = useMutation(MERGE_ORGANIZATION_REQUEST);

  const [
    mergeOrgFunc,
    {
      loading: mergeOrgFuncLoading,
      error: mergeOrgFuncError,
      data: mergeOrgFuncData,
    },
  ] = useMutation(MERGE_ORGANIZATION);

  const [
    getOrganizations,
    {
      loading: organizationsLoading,
      error: organizationsError,
      data: organizationsData,
    },
  ] = useLazyQuery(GET_ORGANIZATIONS, {
    fetchPolicy: "no-cache",
  });

  const [
    getEmployees,
    { loading: employeesLoading, data: employeesData },
  ] = useLazyQuery(GET_EMPLOYEES, {
    fetchPolicy: "no-cache",
  });

  const [
    employeeApproveStatus,
    {
      loading: employeeApproveStatusLoading,
      error: employeeApproveStatusError,
      data: employeeApproveStatusData,
    },
  ] = useMutation(UPDATE_EMPLOYEE_APPROVE, {
    fetchPolicy: "no-cache",
  });

  const callAssetDetailPage = () => {
    history.push({
      pathname: page360Url(assetDetail),
      state: NEGOTIATION_CALLING_ACTION,
    });
  };

  useEffect(() => {
    const userObj = window.localStorage.getItem("user");

    if (userObj) {
      const user = JSON.parse(userObj);

      if (user?.isIndividual) {
        getEmployees({
          variables: {
            skip: 0,
            take: 1000,
            filter: {
              status: true,
            },
          },
        });
      }
    }
  }, []);

  const listOrganizations = () => {
    if (formikRef?.current?.values.comp_name) {
      if (existingOrganization) setExistingOrganization(null);
      if (filterOrganization) setFilterOrganization(null);
      getOrganizations({
        variables: {
          filter: {
            search: formikRef?.current?.values.comp_name,
            isJoinOrganization: true,
          },
        },
      });
    } else {
      if (existingOrganization) setExistingOrganization(null);
      if (filterOrganization) setFilterOrganization(null);
    }
  };

  useEffect(() => {
    if (employeesLoading) setLoading(true);

    if (employeesData) {
      setLoading(false);
      const { count } = employeesData?.getAllEmployees;

      if (count > 0) setIsUsersExist(true);
    }
  }, [employeesLoading, employeesData]);

  useEffect(() => {
    setLoading(true);
    if (organizationsData) {
      setLoading(false);
      setFilterOrganization(null);
      setExistingOrganization(null);
      setIsOrganizationExist(false);
      const { result, count } = organizationsData?.getAllOrganizations;

      if (count > 0) {
        var orgCount = 0;
        var orgData = [];

        for (var i = 0; i < result.length; i++) {
          if (
            result[i].name?.trim().toLowerCase() ===
            formikRef?.current?.values.comp_name.trim().toLowerCase()
          ) {
            orgCount++;

            orgData.push(result[i]);
          }
        }

        // check organization list to user Org list
        const user = JSON.parse(window.localStorage.getItem("user"));

        if (user.userOrg && user.userOrg.length > 0) {
          if (orgCount > 0 && orgCount === 1) {
            for (var i = 0; i < user.userOrg.length; i++) {
              if (!user.userOrg[i].isDeleted) {
                if (user.userOrg[i].organization?.id === orgData[0].id) {
                  toast.error(
                    intl.formatMessage({
                      id: "ASSOCIATE.ORGANIZATION.ALERT",
                    }),
                    {
                      position: toast.POSITION.TOP_RIGHT,
                    },
                  );
                  formikRef &&
                    formikRef.current &&
                    formikRef.current.setFieldValue("comp_name", "");
                } else {
                  setExistingOrganization(orgData[0]);
                }
              }
            }
          }

          if (orgCount > 1) {
            setIsOrganizationExist(true);
            setFilterOrganization(orgData);
          } else if (orgCount > 0 && orgCount === 1) {
            setExistingOrganization(orgData[0]);
          }
        } else {
          if (orgCount > 1) {
            setIsOrganizationExist(true);
            setFilterOrganization(orgData);
          } else if (orgCount > 0 && orgCount === 1) {
            setExistingOrganization(orgData[0]);
          }
        }
      }
    }

    if (organizationsError) setLoading(false);
  }, [organizationsLoading, organizationsData, organizationsError]);

  useEffect(() => {
    if (createOrgLoading) setLoading(true);
    if (createOrgData) {
      const { id } = createOrgData?.createOrganization;

      setLoading(false);

      if (id) {
        const user = JSON.parse(window.localStorage.getItem("user"));

        if (
          (user?.isIndividual ||
            user?.roles.findIndex(val => val?.key === ROLE_GUEST) > -1) &&
          user?.isUserVerified
        ) {
          if (!isOrganizationExist) setIsOrganizationExist(true);
          setNewOrganization(id);
          setFilterOrganization(null);
          if (!isUsersExist) setShowMergeContentAlert(true);
          else {
            currentUserUIProps.setCurrentUser();
            handleModelClose();
          }
        } else {
          currentUserUIProps.setCurrentUser();
          handleModelClose();
        }
      }
    }
       
    if (createOrgError) {
      setLoading(false);
      setError(createOrgError.toString().replace("Error: Error:", ""));
    }
  }, [createOrgLoading, createOrgData, createOrgError]);
  
  useEffect(() => {
    if (joinOrgLoading) setLoading(true);
    if (joinOrgData) {
      setLoading(false);

      const user = JSON.parse(window.localStorage.getItem("user"));

      if (
        (user?.isIndividual ||
          user?.roles.findIndex(val => val?.key === ROLE_GUEST) > -1) &&
        user?.isUserVerified
      ) {
        if (!isOrganizationExist) setIsOrganizationExist(true);
        setFilterOrganization(null);
        if (!isUsersExist) setShowMergeContentAlert(true);
        else {
          currentUserUIProps.setCurrentUser();
          handleModelClose();
        }
      } else {
        currentUserUIProps.setCurrentUser();
        handleModelClose();
      }
    }

    if (joinOrgError) {
      setLoading(false);
      setError(joinOrgError.toString().replace("Error: Error:", ""));
    }
  }, [joinOrgLoading, joinOrgData, joinOrgError]);

  useEffect(() => {
    if (mergeOrgLoading) setLoading(true);
    if (mergeOrgData) {
      handleModelClose();
      callAssetDetailPage();
      currentUserUIProps.setCurrentUser();
      setShowMergeContentAlert(false);
      setLoading(false);
    }

    if (mergeOrgError) {
      setLoading(false);
      let error = mergeOrgError.toString().replace("Error: Error:", "");

      if (
        error.toLowerCase().trim() ===
          intl
            .formatMessage({ id: "GLOBAL.MERGE_NOT_SELLER_ERROR" })
            .toLowerCase()
            .trim() ||
        error.toLowerCase().trim() ===
          intl
            .formatMessage({ id: "GLOBAL.MERGE_NOT_SELLER_ERROR1" })
            .toLowerCase()
            .trim()
      ) {
        currentUserUIProps.setCurrentUser();
        setShowMergeContentAlert(false);
        setIsReqOrgBuyer(true);
      } else setError(error);
    }
  }, [mergeOrgLoading, mergeOrgData, mergeOrgError]);

  useEffect(() => {
    if (mergeOrgFuncLoading) setLoading(true);
    if (mergeOrgFuncData) {
      handleModelClose();
      callAssetDetailPage();
      currentUserUIProps.setCurrentUser();
      setShowMergeContentAlert(false);
      setLoading(false);
    }

    if (mergeOrgFuncError) {
      setLoading(false);
      let error = mergeOrgFuncError.toString().replace("Error: Error:", "");

      if (error === intl.formatMessage({ id: "EMAIL.VERIFY.LINK.MESSAGE" })) {
        currentUserUIProps.setCurrentUser();
        setShowMergeContentAlert(false);
        setIsReqOrgBuyer(true);
      } else setError(error);
    }
  }, [mergeOrgFuncLoading, mergeOrgFuncData, mergeOrgFuncError]);

  useEffect(() => {
    if (employeeApproveStatusLoading) setLoading(true);
    if (employeeApproveStatusError) {
      setLoading(false);
      toast.error(employeeApproveStatusError?.message.toString(), {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

    if (employeeApproveStatusData) {
      setLoading(false);
      toast.success("Organization remove successfully", {
        position: toast.POSITION.TOP_RIGHT,
      });
      handleModelClose();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    employeeApproveStatusData,
    employeeApproveStatusError,
    employeeApproveStatusLoading,
  ]);

  const handleCheckBoxChange = event => {
    setChecked(event.target.checked);
  };

  const handleClickOpen = () => {
    dispatch(setIsShowTNCDialog(true));
  };

  const handleChange = event => {
    setValue(event.target.value);
    setExistingOrganization({ id: event.target.value });
  };

  const showLocationWithOrg = item => {
    if (item?.locations && item.locations?.length > 0) {
      return `${item.name} (${item.locations[0].cityName},${item.locations[0].stateName})`;
    } else {
      return `${item.name}`;
    }
  };

  const renderExistingLayout = () => {
    if (filterOrganization && !showMergeContentAlert) {
      const OrgData = filterOrganization;

      const filteredOrgLocList = [];

      for (let i = 0; i < OrgData.length; i++) {
        let primaryLoc = OrgData[i].locations;

        if (primaryLoc.length > 0) {
          for (let j = 0; j < primaryLoc.length; j++) {
            if (primaryLoc[j].isPrimary === true) {
              filteredOrgLocList.push(OrgData[i]);
            }
          }
        } else {
          filteredOrgLocList.push(OrgData[i]);
        }
      }

      return (
        <>
          <div className="card-body">
            <Typography>
              {
                "There are multiple Companies in our system with the Company Name you have chosen. You can join one of these organization & click the \"Join Organization\" button. You'll then be verified by the Company Admin. If you do not want to join the existing organization, please click on \"Create New Organization\" button."
              }
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                aria-label="org"
                name="org"
                value={value}
                onChange={handleChange}
              >
                <div>
                  {filteredOrgLocList.map((item, index) => {
                    return (
                      <FormControlLabel
                        id={index}
                        value={item.id}
                        control={<Radio />}
                        label={showLocationWithOrg(item)}
                      />
                    );
                  })}
                </div>
              </RadioGroup>
            </FormControl>
          </div>
        </>
      );
    } else if (showMergeContentAlert) {
      return (
        <>
          <Typography>
            Do you wish to merge your existing individual data into the new
            organization?
            <br />
            <br />
          </Typography>
          {!(filterOrganization || existingOrganization) && (
            <Typography>
              <b>Note:</b> Merging data into the new organization will have
              followning impact-
              <br />
              <br />
              <ul>
                <li>
                  Your individual notes & list will be moved to private notes
                </li>
                <li>
                  If you are doing a negotiation on an asset on which the
                  company is already negotiating, all your deals & conversation
                  specific to that asset will be lost
                </li>
              </ul>
            </Typography>
          )}
        </>
      );
    } else if (isReqOrgBuyer) {
      return (
        <>
          <Typography>
            <FormattedMessage id="GLOBAL.MERGE_NOT_SELLER_ERROR" />
            <br />
            <br />
          </Typography>
        </>
      );
    }
  };

  const handleYesSubmit = () => {
    const user = JSON.parse(window.localStorage.getItem("user"));

    if (user) {
      if (existingOrganization) {
        mergeOrg({
          variables: {
            orgId: user?.currentOrganization?.id,
            userId: user?.id || "",
            mergeOrgId: existingOrganization?.id,
          },
        });
      } else {
        mergeOrgFunc({
          variables: {
            orgId: user?.currentOrganization?.id,
            userId: user?.id || "",
            mergeOrgId: newOrganization,
          },
        });
      }
    }
  };

  const handleNoSubmit = () => {
    handleModelClose();
    currentUserUIProps.setCurrentUser();
    setShowMergeContentAlert(false);
  };

  const handleCreateOrgBtn = () => {
    if (checked) {
      // create new organization + join organization
      createOrg({
        variables: {
          name: formikRef.current?.values.comp_name,
        },
      });
    } else {
      toast.error("Please accept Term and condition", {
        position: toast.POSITION.TOP_RIGHT,
      });
      formikRef.current.setSubmitting(false);
    }
  };

  const deleteOrg = () => {
    let tempOrgId;

    if (existingOrganization) tempOrgId = existingOrganization.id;
    else tempOrgId = newOrganization;
    const userOrgObj = user.userOrg.find(x => x.organization.id === tempOrgId);

    employeeApproveStatus({
      variables: {
        userId: user.id,
        isUserVerified: false,
        userOrganizationId: userOrgObj?.id,
        organizationId: tempOrgId,
      },
    });
  };

  const handleOrgBuyerButtonLayout = () => {
    return (
      <>
        <Button
          type="button"
          onClick={handleModelClose}
          className="btn btn-primary btn-elevate mt-5"
        >
          {intl.formatMessage({
            id: "GLOBAL.CONTINUE",
          })}
        </Button>
        <Button
          type="button"
          onClick={deleteOrg}
          className="btn btn-primary btn-elevate mt-5"
        >
          {intl.formatMessage({
            id: "GLOBAL.CANCEL_JOIN_REQUEST",
          })}
        </Button>
      </>
    );
  };

  return (
    <>
      <Modal
        show={showOrgModal}
        onHide={handleModelClose}
        aria-labelledby="example-modal-sizes-title-lg"
        centered
        backdrop="static"
      >
        <SectionLoader loader={loading} />
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({
              id: "JOIN.ADD.ORGANIZATION",
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <>
            {error && (
              <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                <div className="alert-text font-weight-bold">{error}</div>
              </div>
            )}
            <Formik
              innerRef={formikRef}
              enableReinitialize={true}
              initialValues={initialValues}
              validationSchema={organizationModalValidationSchema}
              onSubmit={(values, { setSubmitting }) => {
                if (checked) {
                  if (existingOrganization || filterOrganization) {
                    if (existingOrganization) {
                      joinOrg({
                        variables: {
                          orgId: existingOrganization.id,
                        },
                      });
                    } else {
                      if (filterOrganization) {
                        toast.error(
                          intl.formatMessage({
                            id: "ERROR.SELECT.ORGANIZATION",
                          }),
                          {
                            position: toast.POSITION.TOP_RIGHT,
                          },
                        );
                      }
                    }
                  } else {
                    // create new organization + join organization
                    createOrg({
                      variables: {
                        name: values.comp_name,
                      },
                    });
                  }
                } else {
                  toast.error("Please accept Term and condition", {
                    position: toast.POSITION.TOP_RIGHT,
                  });
                  setSubmitting(false);
                }
              }}
            >
              {({
                handleSubmit,
                handleChange,
                setFieldValue,
                values,
                errors,
                touched,
              }) => (
                <>
                  {!isOrganizationExist &&
                  !showMergeContentAlert &&
                  !isReqOrgBuyer ? (
                      <>
                        <Row>
                          <Col md="12">
                            <Form.Group>
                              <Form.Label>Company Name</Form.Label>
                              <TextField
                                name="comp_name"
                                fullWidth
                                variant="outlined"
                                value={values.comp_name}
                                error={
                                  touched.comp_name && Boolean(errors.comp_name)
                                }
                                helperText={
                                  touched.comp_name ? errors.comp_name : ""
                                }
                                onChange={e => {
                                  handleChange(e);
                                  if (error) setError(null);
                                }}
                                onBlur={listOrganizations}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    ) : (
                      <>{renderExistingLayout()}</>
                    )}
                  {!isReqOrgBuyer && (
                    <Row>
                      <Col md="12">
                        <Form.Group className="termsCheckBox">
                          <Checkbox
                            checked={checked}
                            onChange={handleCheckBoxChange}
                            inputProps={{
                              "aria-label": "primary checkbox",
                            }}
                          />
                          <span onClick={handleClickOpen}>
                            I understand that by submitting any offers, I agree
                            to purchasing the asset, if offer is accepted, and
                            agree to all
                            <a>
                              <u> terms and conditions.</u>
                            </a>
                          </span>
                        </Form.Group>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col md="12" className="customModalFooter mb-5">
                      <Modal.Footer>
                        {showMergeContentAlert ? (
                          <>
                            <Button
                              type="button"
                              onClick={handleYesSubmit}
                              className="btn btn-primary btn-elevate"
                            >
                              Yes
                            </Button>
                            <Button
                              type="button"
                              onClick={handleNoSubmit}
                              className="btn btn-primary btn-elevate"
                            >
                              No
                            </Button>
                          </>
                        ) : !isReqOrgBuyer ? (
                          isOrganizationExist ||
                          existingOrganization ||
                          filterOrganization ? (
                              <>
                                <Button
                                  type="submit"
                                  onClick={handleSubmit}
                                  className="btn btn-primary btn-elevate mt-5"
                                >
                                  {intl.formatMessage({
                                    id: "GLOBAL.JOIN.ORG",
                                  })}
                                </Button>
                                <Button
                                  type="submit"
                                  onClick={handleCreateOrgBtn}
                                  className="btn btn-primary btn-elevate mt-5"
                                >
                                  {intl.formatMessage({
                                    id: "GLOBAL.CREATE.NEW.ORG",
                                  })}
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="submit"
                                onClick={handleSubmit}
                                className="btn btn-primary btn-elevate mt-5"
                              >
                                {intl.formatMessage({
                                  id: "GLOBAL.CREATE.NEW.ORG",
                                })}
                              </Button>
                            )
                        ) : (
                          handleOrgBuyerButtonLayout()
                        )}
                      </Modal.Footer>
                    </Col>
                  </Row>
                </>
              )}
            </Formik>
          </>
        </Modal.Body>
      </Modal>
      {isShowTNCDialog && <TncDialog />}
    </>
  );
};
